import { type ComputedRef, type Ref, ref, computed } from "vue";

import apiSkobo from "@/helpers/api/apiSkobo";

import DriveItem from "@/models/Documents/DriveItem";
import ControlledArea from "@/models/Documents/ControlledArea";
import ControlledAreaDefinition from "@/models/Documents/ControlledAreaDefinition";
import Permission from "@/models/Documents/Permission";

interface CombiDriveFolderDef {
  driveId: string;
  itemId: string;
  name?: string;
  behavior?: { level?: number; tabColor?: string; [key: string]: unknown } | string;
  system?: string;
  group?: string;
  identifier?: string;
  nctSystem?: string;
  nctType?: string;
  nctGroup?: string;
  nctIdentifier?: string;
  nctBehavior?: unknown;
  nctBehaviour?: unknown;
  type?: string;
  [key: string]: unknown;
}

type ControlledAreaKeysShape = { system: string; entity: string; group: string } | null;

interface SpecialItemLike {
  [key: string]: unknown;
}

interface ControlledAreaComposableDependencies {
  driveItem: ComputedRef<(driveId: string, itemId: string) => DriveItem | null>;
  loadDriveItem: (
    driveId: string,
    itemId: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem>;
  reloadDriveItem: (
    driveId: string,
    itemId: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem>;
  reloadDriveItemFromPath: (
    driveId: string,
    path: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem | null>;
  specialItemObjectForDriveItem: ComputedRef<(driveId: string, itemId: string) => SpecialItemLike | null | undefined>;
  loadDocumentPermissions: (driveId: string, itemId: string) => Promise<Permission | null>;
  // Lazy lookups for symbols still defined in the documentFunctions section
  // of index.ts (not yet extracted).
  getIsConfidentialDocumentShareToAulaForUserIdOnDriveId: () => (
    userId: string,
    driveId: string,
    itemId: string
  ) => boolean;
  getIsDocumentJournalizing: () => (appConfId: string, driveId: string, itemId: string) => boolean;
  getIsDocumentLocking: () => (appConfId: string, driveId: string, itemId: string) => boolean;
  getAppConfidentialId: () => string | undefined;
  getReloadConfidentialDocumentsSharedToAulaForUserOnDrive: () => (userId: string, driveId: string) => Promise<unknown>;
}

const caKey = (system: string, entity: string, group: string) => {
  return `${system?.toLowerCase()}_${entity?.toLowerCase()}_${group?.toLowerCase()}`;
};

const splitCaKey = (key: string | undefined): ControlledAreaKeysShape => {
  if (!key) {
    return null;
  }
  const split = key.split("_");
  return { system: split[0], entity: split[1], group: split[2] };
};

export const useControlledAreaComposable = (dependencies: ControlledAreaComposableDependencies) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- shape: { combiDrives: { [system]: { [group]: ControlledAreaDefinition[] } } }; deeply nested with string keys, callers index it via known shape
  const controlledAreaDefinitionMap: Ref<any> = ref(null);
  const controlledAreaMap: Ref<Record<string, ControlledArea[]>> = ref({});
  const combiDriveFolderMap: Ref<Record<string, Record<string, CombiDriveFolderDef[]>>> = ref({});

  const combiDriveFolderContent = computed(() => (system: string, entity: string, group: string, itemId?: string) => {
    itemId = itemId || "root";
    const key = caKey(system, entity, group);
    if (!combiDriveFolderMap.value[key] || !combiDriveFolderMap.value[key][itemId]) {
      return [];
    }

    const listOfChildrenFromHigherLevel: DriveItem[] = [];

    const folders: CombiDriveFolderDef[] = JSON.parse(JSON.stringify(combiDriveFolderMap.value[key][itemId]));
    folders.sort((first, second) => {
      const firstLevel = typeof first?.behavior === "object" ? (first.behavior?.level ?? 0) : 0;
      const secondLevel = typeof second?.behavior === "object" ? (second.behavior?.level ?? 0) : 0;
      return firstLevel - secondLevel;
    });

    folders.forEach((folder: CombiDriveFolderDef) => {
      dependencies.driveItem
        .value(folder.driveId, folder.itemId)
        ?.childrenList?.filter(
          (child: DriveItem) =>
            listOfChildrenFromHigherLevel.map((existing: DriveItem) => existing.name).indexOf(child.name) === -1
        )
        .forEach((child: DriveItem) => listOfChildrenFromHigherLevel.push(new DriveItem(child)));
    });

    const isConfidentialDocumentShareToAulaForUserIdOnDriveId =
      dependencies.getIsConfidentialDocumentShareToAulaForUserIdOnDriveId();
    const isDocumentJournalizing = dependencies.getIsDocumentJournalizing();
    const isDocumentLocking = dependencies.getIsDocumentLocking();
    const appConfidentialId = dependencies.getAppConfidentialId();

    listOfChildrenFromHigherLevel.forEach((item: DriveItem) => {
      if (isConfidentialDocumentShareToAulaForUserIdOnDriveId(entity, item.parentReference.driveId, item.id)) {
        item.nctSharedToAula = true;
      } else {
        item.nctSharedToAula = false;
      }
      item.nctJournalizing = isDocumentJournalizing(appConfidentialId as string, item.parentReference.driveId, item.id);
      item.nctLocking = isDocumentLocking(appConfidentialId as string, item.parentReference.driveId, item.id);
      item.nctSpecialItem = dependencies.specialItemObjectForDriveItem.value(item.parentReference.driveId, item.id);
    });

    return listOfChildrenFromHigherLevel;
  });

  const combiDriveRootDefaultFolder = computed(() => (system: string, entity: string, group: string) => {
    const key = caKey(system, entity, group);
    if (!combiDriveFolderMap.value[key] || !combiDriveFolderMap.value[key]["root"]) {
      return;
    }
    const rootControlledAreaDefault = combiDriveFolderMap.value[key]["root"].find(
      (folder: CombiDriveFolderDef) => typeof folder.behavior === "object" && folder.behavior?.level === 0
    );
    if (!rootControlledAreaDefault) {
      return;
    }
    return dependencies.driveItem.value(rootControlledAreaDefault.driveId, rootControlledAreaDefault.itemId);
  });

  const controlledAreaKeysFromDriveIdAndItemId = computed(() => (driveId: string, itemId: string) => {
    for (const cacheKey of Object.keys(combiDriveFolderMap.value)) {
      const items = Object.values(combiDriveFolderMap.value[cacheKey]) || [];
      for (const cacheItem of items) {
        const found = cacheItem.find(
          (entry: CombiDriveFolderDef) => entry && entry.driveId === driveId && entry.itemId === itemId
        );
        if (found) {
          return splitCaKey(cacheKey);
        }
      }
    }
    return splitCaKey(undefined);
  });

  const controlledAreaRootOfCombiDriveFromDriveIdAndMetas = computed(
    () => (driveId: string, itemId: string, system: string, identifier: string, group: string) => {
      let key: string | undefined;
      Object.keys(combiDriveFolderMap.value).every(outerKey => {
        Object.keys(combiDriveFolderMap.value[outerKey]).every(folderKey => {
          Object.keys(combiDriveFolderMap.value[outerKey][folderKey]).every((indexKey: string) => {
            const index = Number(indexKey);
            const folder = combiDriveFolderMap.value[outerKey][folderKey][index];
            if (folder && folder.driveId === driveId && folder.itemId === itemId) {
              key = outerKey;
              return false;
            }
            return true;
          });
          if (key) {
            return false;
          }
          return true;
        });
        if (key) {
          return false;
        }
        return true;
      });
      if (!key) {
        return undefined;
      }
      return controlledAreaMap.value[key].find(
        (area: ControlledArea) => area.system === system && area.identifier === identifier && area.group === group
      );
    }
  );

  const controlledAreaFromDriveIdAndItemId = computed(() => (driveId: string, itemId: string) => {
    const controlledAreaMapKeys = Object.keys(controlledAreaMap.value);
    for (let i = 0; i < controlledAreaMapKeys.length; i++) {
      const found = controlledAreaMap.value[controlledAreaMapKeys[i]].find(
        (caItem: ControlledArea) => caItem.driveId === driveId && caItem.itemId === itemId
      );
      if (found) {
        return found;
      }
    }
    return undefined;
  });

  const rootControlledAreaDefinitionFromDriveIdAndItemId = computed(() => (driveId: string, itemId: string) => {
    let groupKey: string | undefined;
    Object.keys(controlledAreaMap.value).forEach(key => {
      if (
        controlledAreaMap.value[key].find(
          (caItem: ControlledArea) => caItem.driveId === driveId && caItem.itemId === itemId
        )
      ) {
        groupKey = key;
      }
    });
    if (groupKey) {
      return controlledAreaMap.value[groupKey].find((caItem: ControlledArea) => caItem.type === "RootFolder");
    }
    return null;
  });

  const controlledAreaDriveId = computed(() => (system: string, entity: string, group: string) => {
    const key = caKey(system, entity, group);
    if (controlledAreaMap.value[key]) {
      return controlledAreaMap.value[key][0]?.driveId;
    }
    return undefined;
  });

  const controlledAreaConfidentialId = computed(() => (system: string, entity: string, group: string) => {
    const key = caKey(system, entity, group);
    if (controlledAreaMap.value[key]) {
      return controlledAreaMap.value[key].find((area: ControlledArea) => area.type === "RootFolder")?.confidentialId;
    }
    return undefined;
  });

  const defaultToolbarControlledArea = computed(() => (system: string, group: string, identifier: string) => {
    if (
      controlledAreaDefinitionMap.value &&
      controlledAreaDefinitionMap.value["combiDrives"] &&
      controlledAreaDefinitionMap.value["combiDrives"][system?.toLowerCase()] &&
      controlledAreaDefinitionMap.value["combiDrives"][system?.toLowerCase()][group?.toLowerCase()]
    ) {
      const definition = (
        controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()][
          group.toLowerCase()
        ] as ControlledAreaDefinition[]
      ).find((def: ControlledAreaDefinition) => def.identifier === identifier);
      return definition?.toolbarDefault;
    }
    return null;
  });

  const controlledAreaGroupsForSystem = computed(() => (system: string) => {
    if (
      controlledAreaDefinitionMap.value &&
      controlledAreaDefinitionMap.value["combiDrives"] &&
      controlledAreaDefinitionMap.value["combiDrives"][system?.toLowerCase()]
    ) {
      const groupKeys = Object.keys(controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()]).sort(
        (first, second) => {
          return (
            controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()][first][0]?.groupSort -
            controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()][second][0]?.groupSort
          );
        }
      );
      return groupKeys.map(groupKey => ({
        name: groupKey,
        color:
          controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()][groupKey][0]?.behavior?.tabColor ||
          null
      }));
    }
    return undefined;
  });

  const loadControlledAreaDefinitions = async () => {
    try {
      if (!controlledAreaDefinitionMap.value) {
        const response = await apiSkobo.get("/appconfidentialfolder/controlledareas");

        type CombiDrivesMap = {
          combiDrives: Record<string, Record<string, ControlledAreaDefinition[]>>;
        };
        const map: CombiDrivesMap = { combiDrives: {} };
        const apiCombiDrives = response.data.combiDrives as Record<string, Record<string, unknown[]>>;
        Object.keys(apiCombiDrives).forEach(system => {
          map["combiDrives"][system.toLowerCase()] = {};
          Object.keys(apiCombiDrives[system]).forEach(group => {
            map["combiDrives"][system.toLowerCase()][group.toLowerCase()] = apiCombiDrives[system][group].map(
              (definition: unknown) => new ControlledAreaDefinition(definition)
            );
          });
        });

        controlledAreaDefinitionMap.value = map;
      }
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.error(caughtError);
    }
  };

  const reloadControlledArea = async (system: string, entity: string, group: string) => {
    try {
      const response = await apiSkobo.get(`/appconfidentialfolder/combidrive/${system}/${entity}/${group}`);
      if (response.data) {
        controlledAreaMap.value[caKey(system, entity, group)] = (response.data as unknown[]).map(
          (area: unknown) => new ControlledArea(area)
        );
      }
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.log(caughtError);
    }
  };

  const loadControlledArea = async (system: string, entity: string, group: string) => {
    if (!controlledAreaMap.value[caKey(system, entity, group)]) {
      await reloadControlledArea(system, entity, group);
    }
    return controlledAreaMap.value[caKey(system, entity, group)];
  };

  const reloadCombiDriveRoot2 = async (subFolders: ControlledArea[]) => {
    const promises = subFolders.map(async (subFolder: ControlledArea) => {
      const driveItem = await dependencies.reloadDriveItem(subFolder.driveId, subFolder.itemId, {
        nctType: subFolder.type,
        nctIdentifier: subFolder.identifier,
        nctSystem: subFolder.system,
        nctBehavior: subFolder.behavior,
        nctGroup: subFolder.group
      });
      if (!driveItem) {
        return;
      }
      return {
        driveId: driveItem.parentReference.driveId,
        itemId: driveItem.id,
        name: driveItem.name,
        behavior: subFolder.behavior,
        system: subFolder.system,
        group: subFolder.group,
        identifier: subFolder.identifier
      };
    });
    return await Promise.all(promises);
  };

  const reloadCombiDriveSubFolder = async (
    subFolders: ControlledArea[],
    itemId: string
  ): Promise<CombiDriveFolderDef[]> => {
    if (!subFolders.length) {
      return [];
    }

    try {
      const foldersCombiningSelectedFolder: CombiDriveFolderDef[] = [];
      const selectedDriveItem = await dependencies.reloadDriveItem(subFolders[0].driveId, itemId);

      if (!selectedDriveItem || (selectedDriveItem as DriveItem & { error?: unknown }).error) {
        return [];
      }

      foldersCombiningSelectedFolder.push({
        driveId: selectedDriveItem.parentReference.driveId,
        itemId: selectedDriveItem.id,
        name: selectedDriveItem.name,
        behavior: selectedDriveItem.nctBehavior,
        system: subFolders[0].system,
        group: subFolders[0].group,
        identifier: selectedDriveItem.nctIdentifier
      });

      const definitionToStartFrom = subFolders.find(
        (folder: ControlledArea) => folder.identifier === selectedDriveItem.nctIdentifier
      );
      if (definitionToStartFrom) {
        const driveItemToStartFrom = await dependencies.loadDriveItem(
          definitionToStartFrom.driveId,
          definitionToStartFrom.itemId,
          {
            nctType: definitionToStartFrom.type,
            nctIdentifier: definitionToStartFrom.identifier,
            nctSystem: definitionToStartFrom.system,
            nctBehavior: definitionToStartFrom.behavior,
            nctGroup: definitionToStartFrom.group
          }
        );

        const regexStart = "^https:\\/\\/.*\\/sites(\\/[a-zA-Z0-9-%_]*){2}(.*)(\\/";
        const regexEnd = ")(\\/.*)$";
        const regex = new RegExp(regexStart + encodeURI(driveItemToStartFrom.name) + regexEnd, "g");

        const otherDefitions = subFolders.filter(
          (folder: ControlledArea) => folder.identifier !== selectedDriveItem.nctIdentifier
        );

        if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
          const regexResult = regex.exec(encodeURI(selectedDriveItem.webUrl))!;
          const postCombiDrivePath = regexResult[4];
          const pathSplit = postCombiDrivePath.split("/");

          const promises = otherDefitions.map(async (folder: ControlledArea) => {
            let lastSplitLeg: (DriveItem & { error?: unknown }) | null = await dependencies.reloadDriveItem(
              folder.driveId,
              folder.itemId,
              {
                nctType: folder.type,
                nctIdentifier: folder.identifier,
                nctSystem: folder.system,
                nctBehavior: folder.behavior,
                nctGroup: folder.group
              }
            );

            if (!lastSplitLeg || lastSplitLeg.error) {
              return null;
            }

            for (let i = 1; i < pathSplit.length; i++) {
              const nextSplitLeg = lastSplitLeg.childrenList.find(
                (child: DriveItem) => encodeURI(child.name) === pathSplit[i]
              );
              if (!nextSplitLeg) {
                lastSplitLeg = null;
                break;
              }
              const currentSplitLeg = await dependencies.reloadDriveItem(
                nextSplitLeg.parentReference.driveId,
                nextSplitLeg.id,
                {
                  nctType: folder.type,
                  nctIdentifier: folder.identifier,
                  nctSystem: folder.system,
                  nctBehavior: folder.behavior,
                  nctGroup: folder.group
                }
              );
              if (!currentSplitLeg || (currentSplitLeg as DriveItem & { error?: unknown }).error) {
                lastSplitLeg = null;
                break;
              }
              lastSplitLeg = currentSplitLeg;
            }
            return lastSplitLeg;
          });

          const children = (await Promise.all(promises)).filter((child): child is DriveItem => !!child);
          foldersCombiningSelectedFolder.push(
            ...children.map((child: DriveItem) => ({
              driveId: child.parentReference.driveId,
              itemId: child.id,
              name: child.name
            }))
          );
        } else {
          const regexResult = regex.exec(selectedDriveItem.webUrl)!;
          const preCombiDrivePath = regexResult[2];
          const postCombiDrivePath = regexResult[4];

          const promises = otherDefitions
            .map(async (folder: ControlledArea) => {
              const otherItem = await dependencies.reloadDriveItemFromPath(
                definitionToStartFrom.driveId,
                preCombiDrivePath + "/" + folder.identifier + postCombiDrivePath,
                {
                  nctType: folder.type,
                  nctIdentifier: folder.identifier,
                  nctSystem: folder.system,
                  nctBehavior: folder.behavior,
                  nctGroup: folder.group
                }
              );

              if (otherItem && otherItem.children) {
                return {
                  driveId: otherItem.parentReference.driveId,
                  itemId: otherItem.id,
                  name: otherItem.name,
                  behavior: folder.behavior,
                  system: folder.system,
                  group: folder.group,
                  identifier: folder.identifier
                };
              }
            })
            .filter((promise): promise is Promise<CombiDriveFolderDef> => !!promise);

          foldersCombiningSelectedFolder.push(
            ...(await Promise.all(promises)).filter((folder): folder is CombiDriveFolderDef => !!folder)
          );
        }
      }

      return foldersCombiningSelectedFolder;
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.error(caughtError);
      return [];
    }
  };

  const reloadCombiDriveFolder = async (system: string, entity: string, group: string, itemId?: string) => {
    itemId = itemId || "root";
    try {
      const subFolders =
        (await loadControlledArea(system, entity, group))?.filter(
          (area: ControlledArea) => area.type === "SubFolder"
        ) || [];

      if (subFolders.length && subFolders[0].group === "barn") {
        const reloadConfidentialDocumentsSharedToAulaForUserOnDrive =
          dependencies.getReloadConfidentialDocumentsSharedToAulaForUserOnDrive();
        reloadConfidentialDocumentsSharedToAulaForUserOnDrive(entity, subFolders[0].driveId);
      }

      await loadControlledAreaDefinitions();
      subFolders.forEach((subFolder: ControlledArea) => {
        if (!subFolder.behavior || typeof subFolder.behavior === "string") {
          const cad = (
            controlledAreaDefinitionMap.value["combiDrives"][system.toLowerCase()][
              group.toLowerCase()
            ] as ControlledAreaDefinition[]
          ).find((def: ControlledAreaDefinition) => def.identifier === subFolder.identifier);
          subFolder.behavior = cad?.behavior;
        }
      });

      let folderDefinitions: CombiDriveFolderDef[] = [];
      if (itemId === "root") {
        folderDefinitions = (await reloadCombiDriveRoot2(subFolders)).filter(
          (folder): folder is CombiDriveFolderDef => !!folder
        );
      } else {
        folderDefinitions = await reloadCombiDriveSubFolder(subFolders, itemId);
      }

      if (!combiDriveFolderMap.value[caKey(system, entity, group)]) {
        combiDriveFolderMap.value[caKey(system, entity, group)] = {};
      }
      combiDriveFolderMap.value[caKey(system, entity, group)][itemId] = folderDefinitions;
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.error(caughtError);
    }
    loadCombiDriveFolder(system, entity, group);
  };

  const loadCombiDriveFolder = async (system: string, entity: string, group: string, itemId?: string) => {
    itemId = itemId || "root";
    if (
      !combiDriveFolderMap.value[caKey(system, entity, group)] ||
      !combiDriveFolderMap.value[caKey(system, entity, group)][itemId]
    ) {
      await reloadCombiDriveFolder(system, entity, group, itemId);
    }
  };

  const calculateCombiDriveFoldersThatAreWritable = async (
    system: string,
    entity: string,
    group: string,
    itemId?: string
  ) => {
    itemId = itemId || "root";
    await loadCombiDriveFolder(system, entity, group, itemId);
    const content = combiDriveFolderContent.value(system, entity, group, itemId);
    const promises = content
      .filter((folder: DriveItem & { isFolder?: boolean }) => folder.isFolder)
      .map(async (folder: DriveItem) => {
        const permission = await dependencies.loadDocumentPermissions(folder.parentReference.driveId, folder.id);
        if (permission && permission.myPermission >= 2) {
          return folder;
        }
        return null;
      });
    return await Promise.all(promises).then(folders => folders.filter((folder): folder is DriveItem => !!folder));
  };

  return {
    controlledAreaDefinitionMap,
    controlledAreaMap,
    combiDriveFolderMap,
    combiDriveFolderContent,
    combiDriveRootDefaultFolder,
    controlledAreaKeysFromDriveIdAndItemId,
    controlledAreaRootOfCombiDriveFromDriveIdAndMetas,
    controlledAreaFromDriveIdAndItemId,
    rootControlledAreaDefinitionFromDriveIdAndItemId,
    controlledAreaDriveId,
    controlledAreaConfidentialId,
    defaultToolbarControlledArea,
    controlledAreaGroupsForSystem,
    loadControlledAreaDefinitions,
    loadControlledArea,
    reloadControlledArea,
    loadCombiDriveFolder,
    reloadCombiDriveFolder,
    reloadCombiDriveRoot2,
    reloadCombiDriveSubFolder,
    calculateCombiDriveFoldersThatAreWritable
  };
};
